<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { onUnauthorized } from './api/http'
import LoginGate from './components/LoginGate.vue'
import SignedOut from './components/SignedOut.vue'
import AiHandoffAlerts from './components/aimode/AiHandoffAlerts.vue'
import HealthBanner from './components/health/HealthBanner.vue'
import NewVersionBar from './components/NewVersionBar.vue'
import { useInbox } from './stores/inbox'
import { useSession } from './stores/session'
import { rememberReturn, takeReturn } from './lib/chatLink'
import { navigating, prefetchPages } from './lib/pagePrefetch'
import { pruneReplyDrafts } from './lib/replyDrafts'
import { pageAllowed } from './lib/adsView'
import { affiliatePageAllowed } from './lib/affiliate'
import { reviewerPageAllowed } from './lib/metaConnect'

const store = useInbox()
const session = useSession()
const router = useRouter()
const route = useRoute()
// /login = the Meta App Review reviewer's sign-in while the owner has it on (api/app/reviewer.py), else the POS screen
const atLogin = computed(() => route.name === 'login')
const SSO_REASONS = new Set(['expired', 'used', 'invalid', 'inactive', 'off'])
// pages a Meta reviewer never gets (their data 404s on the server anyway): back to the chats — of the settings only
// ช่องทางที่เชื่อม, read only + the "Continue with Facebook" dry run (lib/metaConnect.ts reviewerPageAllowed)
// owner-only pages (โฆษณา): anyone else goes back to the chats, like the reviewer above · พาร์ทเนอร์: owner + head
const blocked = (name: unknown, section?: unknown): boolean =>
  (!!session.me?.reviewer && !reviewerPageAllowed(String(name ?? ''), section)) ||
  !pageAllowed(String(name ?? ''), session.me) ||
  !affiliatePageAllowed(String(name ?? ''), session.me)
const removeGuard = router.beforeEach((to) => (blocked(to.name, to.params.section) ? { path: '/' } : true))

// Signed in (POS menu "แชทรวม" → /auth/sso cookie, or a reviewer at /login) → inbox; otherwise the sign-in screen.
watch(
  () => session.state,
  async (s, prev) => {
    if (s === 'in') {
      if (session.me?.reviewer) store.scope = 'team' // the test Page's chats are nobody's yet: show the whole list
      void store.init()
      prefetchPages(router) // every page ahead of time, when idle: clicks on the rail switch instantly
      // a chat link opened before signing in (POS round trip lands on "/"): open that chat now
      const back = takeReturn()
      if (back && router.currentRoute.value.path === '/') void router.replace(back)
      await router.isReady()
      // signed in at /login → the chat the reviewer was on before timing out, or the inbox
      if (router.currentRoute.value.name === 'login') void router.replace(back ?? '/')
      else if (blocked(router.currentRoute.value.name, router.currentRoute.value.params.section)) {
        void router.replace('/') // a page load straight onto a page this person does not get (reviewer · owner only)
      }
    } else if (prev === 'in') store.dispose()
    if (s === 'out' || s === 'error') rememberReturn(router.currentRoute.value.path)
    // the reviewer signed out / timed out / reloaded after that: their own sign-in card, not the POS one
    // (not after a POS sign-in link that failed: that screen says why and links back to the POS)
    if (s === 'out' && session.wasReviewer && !SSO_REASONS.has(session.reason ?? '')) {
      await router.isReady()
      if (router.currentRoute.value.name !== 'login') void router.replace({ name: 'login' })
    }
  },
)

onMounted(async () => {
  pruneReplyDrafts() // unsent replies older than 7 days (lib/replyDrafts.ts)
  session.takeReasonFromUrl()
  onUnauthorized(() => session.lost())
  void session.check()
  // drop ?sso= so a reload does not repeat the message (through the router, which owns the URL)
  await router.isReady()
  const { sso, ...rest } = router.currentRoute.value.query
  if (sso !== undefined) void router.replace({ query: rest, hash: router.currentRoute.value.hash })
})
onBeforeUnmount(() => {
  removeGuard()
  onUnauthorized(null)
  store.dispose()
})
</script>

<template>
  <template v-if="session.state === 'in'">
    <div v-if="navigating" class="boot nav" role="status" aria-label="กำลังเปิดหน้า"><span></span></div>
    <RouterView />
    <!-- "AI ส่งต่อแชทให้คุณ" on every page (dashboard, settings …), with the chime (docs/ai-mode.md) -->
    <AiHandoffAlerts />
    <!-- "บริการล่ม N รายการ · ดูหน้า Health" for owner/head (stores/health.ts polls; nothing for staff) -->
    <HealthBanner />
    <!-- "มีระบบเวอร์ชันใหม่ · โหลดหน้าใหม่" when a newer build is live (lib/staleBuild.ts) -->
    <NewVersionBar />
  </template>
  <LoginGate
    v-else-if="(session.state === 'out' || session.state === 'error') && atLogin"
    :reason="session.reason"
    :failed="session.state === 'error'"
    @retry="session.check()"
    @signed-in="session.check()"
  />
  <SignedOut
    v-else-if="session.state === 'out' || session.state === 'error'"
    :reason="session.reason"
    :failed="session.state === 'error'"
    @retry="session.check()"
  />
  <div v-else class="boot" role="status" aria-label="กำลังโหลด"><span></span></div>
</template>

<style scoped>
.boot {
  position: fixed;
  inset: 0 0 auto;
  height: 3px;
  overflow: hidden;
}
.boot.nav {
  z-index: 70;
}
.boot span {
  display: block;
  width: 30%;
  height: 100%;
  background: var(--ac);
  animation: boot 1.1s ease-in-out infinite;
}
@keyframes boot {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(340%);
  }
}
</style>
